import { useCallback, useEffect, useMemo, useRef } from 'react';
import { compact, groupBy, isEmpty, isEqual, maxBy, range, sumBy, uniq } from 'lodash';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  type BlockId,
  blockInfoGraphicType,
  blockLimitedRequestSubjects,
  blockSettings,
  multiLevelPortfolioStrategyFilterType,
  pieChartLegendPosition,
} from 'venn-state';
import { type CustomRow, type PortfolioRowData } from '../../../studio-blocks/types';
import {
  type MultiLevelPortfolioFilterSelection,
  PortfolioStrategyFilterType,
} from '../../../studio-blocks/components/grid/types';

import { isPortfolioRowData } from '../../../studio-blocks/logic/typeUtils';
import type { Portfolio } from 'venn-api';
import { useHasFF } from 'venn-utils';

// Deliberately unusual separator to avoid collision with portfolio names in path keys.
const SEP = '|-|=|-|';

const getLevelsBasedOnFilter = (
  filterType: PortfolioStrategyFilterType | undefined,
  allLevels: (string | number)[],
  customSelection: (string | number)[],
) => {
  if (filterType === PortfolioStrategyFilterType.DEFAULT || !filterType) {
    return allLevels;
  }

  return filterType === PortfolioStrategyFilterType.ALL_STRATEGIES
    ? allLevels.filter((l) => l !== 'investments')
    : customSelection;
};

const collectPortfolioIds = (node: Portfolio): number[] => [
  node.id,
  ...(node.children ?? []).flatMap(collectPortfolioIds),
];

const getLevels = (portfolio: Portfolio | undefined) => {
  if (!portfolio) {
    return [0, 'investments'];
  }

  const maxDepth = (node: Portfolio, depth: number): number =>
    // Fund nodes are leaf investments, not strategy containers — exclude them from depth count.
    Math.max(depth, ...(node.children ?? []).filter((c) => !c.fund).map((c) => maxDepth(c, depth + 1)));

  return [...range(0, maxDepth(portfolio, 0)), 'investments'];
};

/**
 * For any rows with paths, build a map of the final key to all earlier paths.  This will be used later to replace the names
 * anywhere they occur and append an index to the end to deduplicate.
 */
const buildItemParentPrefixes = <T,>(rows: PortfolioRowData<T>[]) => {
  const map: Record<string, string[]> = {};

  rows.forEach((row) => {
    if (!Array.isArray(row.path) || row.path.length === 0) {
      return;
    }

    row.path.forEach((rawItem, idx) => {
      const item = String(rawItem);
      const parentPrefix = row.path
        .slice(0, idx)
        .map((p) => String(p))
        .join(SEP); // '' for root
      (map[item] ||= []).push(parentPrefix);
    });
  });

  return map;
};

/**
 * This will update the paths of portfolio rows to ensure uniqueness based on their path so that if we ever do filtering
 * and remove some of the earlier levels we don't end up with duplicates which Ag-Grid filters out.  For example:
 * [
 *   ['A', 'B', 'C(0)'],
 *   ['D', 'E', 'C(0)']
 * ] when filtered for the investments only will be:
 * [
 *   ['C(0)'],
 *   ['C(0)']
 * ]
 * But after this change will be:
 * [
 *   ['C(0)'],
 *   ['C(0)_1']
 * ]
 */
const updatePathsToPreventRowMerges = (rows: CustomRow[]) => {
  const itemParentPrefixes = buildItemParentPrefixes(rows.filter((row) => isPortfolioRowData(row)));

  return rows.map((row) => {
    if (!isPortfolioRowData(row) || !row.path || row.path.length === 0) {
      return row;
    }

    // this will update the paths to de-duplicate them and re-map them appending an index
    // starting for each sub-path till the end.  So: ['A', 'B', 'C'] might become ['A_3', 'B', 'C_2']
    // based on how many duplicates of A and C there are in the full list of rows.
    const newPath = row.path.map((item, idx) => {
      const parentPrefix = row.path.slice(0, idx).join(SEP);
      const candidates = itemParentPrefixes[item] || [];
      const occurrenceIndex = candidates.indexOf(parentPrefix);
      return occurrenceIndex > 0 ? `${item}_${occurrenceIndex}` : item;
    });

    return { ...row, path: newPath };
  });
};

type ValueObject = Record<string, unknown>;

/**
 * When merging rows if they are portfolio rows we require that they have a suffix of (0), (1), etc to ensure uniqueness.
 * This will remove the suffixes so the updated aggregation logic can work to merge and sum them as needed.
 */
const removePathSuffixes = (rows: CustomRow[]) => {
  return rows.map((row) => {
    if (!isPortfolioRowData(row) || !row.path || row.path.length === 0) return row;

    const newPath = row.path.map((item) => String(item).replace(/\(\d+\)$/, ''));

    return { ...row, path: newPath };
  });
};

/**
 * Sums up the rows that have the same path to merge them.  If any fields are non-numeric and not the same across the rows
 * they will be set to '--'.
 */
const mergeRows = (rows: CustomRow[], allNonSummable: boolean) => {
  const grouped = groupBy(
    rows.filter((row) => isPortfolioRowData(row)),
    (row) => row.path?.join(SEP) ?? row.key ?? '',
  );

  const result: CustomRow[] = [];
  const processedKeys = new Set<string>();

  rows.forEach((row) => {
    if (!isPortfolioRowData(row)) {
      result.push(row);
      return;
    }

    const pathKey = row.path?.join(SEP) ?? row.key ?? '';
    if (processedKeys.has(pathKey)) return;
    processedKeys.add(pathKey);

    const groupedRows = grouped[pathKey] ?? [];
    if (!row.value?.[0] || groupedRows.length === 1) {
      result.push(row);
      return;
    }

    const firstValue = row.value[0] as ValueObject;
    const numericKeys = Object.keys(firstValue).filter((key) => !allNonSummable && typeof firstValue[key] === 'number');
    const nonNumericKeys = Object.keys(firstValue).filter(
      (key) => allNonSummable || typeof firstValue[key] !== 'number',
    );

    const summedValues: ValueObject = {
      ...Object.fromEntries(
        numericKeys.map((key) => [
          key,
          sumBy(groupedRows, (r) => {
            const obj = r.value?.[0] as ValueObject | undefined;
            return typeof obj?.[key] === 'number' ? obj[key] : 0;
          }),
        ]),
      ),
      ...Object.fromEntries(
        nonNumericKeys.map((key) => {
          const values = groupedRows.map((r) => {
            const obj = r.value?.[0] as ValueObject | undefined;
            return obj?.[key];
          });
          const allSame = values.every((v) => v === values[0]);
          return [key, allSame ? values[0] : '--'];
        }),
      ),
    };

    result.push({
      ...row,
      value: [summedValues],
    });
  });

  return result;
};

export const usePortfolioMultiFiltering = (blockId: BlockId) => {
  const [filter, setFilter] = useRecoilState(multiLevelPortfolioStrategyFilterType(blockId));
  const [legendPosition, setLegendPosition] = useRecoilState(pieChartLegendPosition(blockId));
  const wereLevelsFiltered = filter?.maxTruncatedLevel !== undefined;
  const subjects = useRecoilValue(blockLimitedRequestSubjects(blockId));
  const settings = useRecoilValue(blockSettings(blockId));
  const infoGraphicType = useRecoilValue(blockInfoGraphicType(blockId));
  const blockType = settings.customBlockType;
  const isFPI =
    (infoGraphicType?.includes('PIVOTED_GRID') && blockType === 'FINANCIAL_PERFORMANCE_INDICATORS') || false;
  const hasMultiLevelPortfolioFiltering =
    useHasFF('multilevel_portfolio_filtering_ff') &&
    (blockType === 'PORTFOLIO_BREAKDOWN' ||
      blockType === 'PORTFOLIO_COMPARISON' ||
      blockType === 'TAX_IMPACT' ||
      isFPI ||
      (infoGraphicType?.includes('GRID') && blockType === 'PORTFOLIO_CONTRIBUTION'));

  const levelSelectionOnly = blockType === 'PORTFOLIO_BREAKDOWN'; // single portfolio for donut chart
  const supportsMergeLevels = blockType !== 'FINANCIAL_PERFORMANCE_INDICATORS' && blockType !== 'TAX_IMPACT';

  const portfolios = useMemo(
    () => compact(levelSelectionOnly ? [subjects?.[0]?.portfolio] : subjects?.map((s) => s.portfolio)),
    [subjects, levelSelectionOnly],
  );

  const allLevels = useMemo(() => {
    // Use the deepest portfolio so the filter UI exposes every available level across all subjects.
    return maxBy(portfolios.map(getLevels), 'length') ?? [];
  }, [portfolios]);

  const strategyLevels = useMemo(() => allLevels.filter((l) => l !== 'investments') as number[], [allLevels]);

  const effectiveFilterLevels = useMemo(() => {
    const filterLevels = filter?.levels ?? [];
    if (filter?.filterType !== PortfolioStrategyFilterType.CUSTOM_SELECTION) {
      return filterLevels;
    }

    const filterStrategyLevels = filterLevels.filter((l): l is number => l !== 'investments');

    const intersected: (string | number)[] = filterStrategyLevels.filter((l) => strategyLevels.includes(l));
    if (filterLevels.includes('investments') && allLevels.includes('investments')) {
      intersected.push('investments');
    }

    return intersected;
  }, [filter?.levels, filter?.filterType, strategyLevels, allLevels]);

  const isEmptyOrNoSubjects =
    isEmpty(portfolios) || portfolios.map((p) => (p?.children?.length ?? 0) === 0).every((v) => v);

  const filterRowData = useCallback(
    (rawRowDataIn: CustomRow[]) => {
      const mergeRowsEnabled: boolean = (filter?.mergeRows && supportsMergeLevels) || isFPI;
      const rawRowData = mergeRowsEnabled ? removePathSuffixes(rawRowDataIn) : rawRowDataIn;
      // Before the multilevel FF, the atom stored the filterType enum directly (not wrapped in an object).
      const filterType =
        (hasMultiLevelPortfolioFiltering ? filter?.filterType : filter) ?? PortfolioStrategyFilterType.DEFAULT;
      const levels = uniq(hasMultiLevelPortfolioFiltering ? effectiveFilterLevels : []);
      levels.sort((a, b) => (a === 'investments' ? 1 : b === 'investments' ? -1 : (a as number) - (b as number)));

      const filtered = compact(
        (!mergeRowsEnabled ? updatePathsToPreventRowMerges(rawRowData) : rawRowData).map((row) => {
          if (
            !filterType ||
            filterType === PortfolioStrategyFilterType.DEFAULT ||
            !isPortfolioRowData(row) ||
            row.isMetadata
          ) {
            return row;
          }

          if (!row.isStrategy && !levels.includes('investments')) {
            return undefined;
          }

          if (row.isStrategy && filterType === PortfolioStrategyFilterType.ALL_STRATEGIES) {
            return row;
          }

          const filteredPath = row.path.filter(
            (_, idx) =>
              levels.includes(idx) ||
              (levels.includes('investments') && idx === row.path.length - 1 && !row.isStrategy),
          );

          // when filtering strategies, only include strategies if they are included at a filtered level, so strategies
          // that are lower would be included if we didn't make the check on the raw row to confirm that the strategy is
          // a termination level from those included
          if (
            row.isStrategy &&
            filteredPath.length > 0 &&
            row.path.length > 0 &&
            !levels.includes(row.path.length - 1)
          ) {
            return undefined;
          }

          if (isEmpty(filteredPath)) {
            return undefined;
          }
          return {
            ...row,
            path: filteredPath,
          };
        }),
      );

      const removeNonParentedInvestments = filtered.filter((row) => {
        if (
          !isPortfolioRowData(row) ||
          row.isStrategy ||
          isEmpty(effectiveFilterLevels) ||
          (effectiveFilterLevels.length === 1 && effectiveFilterLevels[0] === 'investments') ||
          filter?.filterType !== PortfolioStrategyFilterType.CUSTOM_SELECTION ||
          !row.path ||
          row.path.length === 0
        ) {
          return true;
        }

        // if the last item in the path is investments and there is more than one item in the path
        // then we need to check that the parent path exists in the filtered list
        if (!row.isStrategy) {
          const parentPath = row.path.slice(0, row.path.length - 1).join(SEP);
          return filtered.some(
            (r) => isPortfolioRowData(r) && r.isStrategy && (r.path?.join(SEP) ?? r.key ?? '') === parentPath,
          );
        }

        return false;
      });

      return mergeRowsEnabled ? mergeRows(removeNonParentedInvestments, isFPI) : removeNonParentedInvestments;
    },
    [filter, effectiveFilterLevels, hasMultiLevelPortfolioFiltering, isFPI, supportsMergeLevels],
  );

  /**
   * Update the filter but also the proper non-custom filter type if the level selection implies it
   */
  const updateFilter = useCallback(
    (newFilter: typeof filter) => {
      const newLevels = [...(newFilter?.levels ?? [])].sort((a, b) =>
        a === 'investments' ? 1 : b === 'investments' ? -1 : (a as number) - (b as number),
      );

      if (levelSelectionOnly) {
        // For Portfolio Composition (PORTFOLIO_BREAKDOWN) we don't have filter type radio buttons, we always treat it as custom
        setFilter(newFilter);
        return;
      }

      if (isEqual(newLevels, allLevels)) {
        setFilter({
          filterType: PortfolioStrategyFilterType.DEFAULT,
          maxTruncatedLevel: newFilter?.maxTruncatedLevel,
        });
        return;
      }

      if (isEqual(newLevels, strategyLevels)) {
        setFilter({
          filterType: PortfolioStrategyFilterType.ALL_STRATEGIES,
          maxTruncatedLevel: newFilter?.maxTruncatedLevel,
        });
        return;
      }

      setFilter(newFilter);
    },
    [levelSelectionOnly, allLevels, strategyLevels, setFilter],
  );

  const toggleMergeRows = useCallback(
    () =>
      setFilter((prev) => ({
        ...(prev ?? { filterType: PortfolioStrategyFilterType.DEFAULT }),
        mergeRows: !prev?.mergeRows,
      })),
    [setFilter],
  );

  const isTaxImpact = blockType === 'TAX_IMPACT';

  useEffect(() => {
    if (filter) {
      return;
    }

    if (levelSelectionOnly) {
      if (isEmpty(portfolios)) {
        return;
      }
      setFilter({
        filterType: PortfolioStrategyFilterType.CUSTOM_SELECTION,
        levels: strategyLevels.length > 0 ? [0] : ['investments'],
      });
      return;
    }

    if (isTaxImpact) {
      setFilter({ filterType: PortfolioStrategyFilterType.CUSTOM_SELECTION, levels: ['investments'] });
    }
  }, [filter, setFilter, levelSelectionOnly, isTaxImpact, portfolios, strategyLevels]);

  return {
    filter: {
      filterType: filter?.filterType ?? PortfolioStrategyFilterType.DEFAULT,
      levels: getLevelsBasedOnFilter(filter?.filterType, allLevels, effectiveFilterLevels),
      mergeRows: filter?.mergeRows,
    } satisfies MultiLevelPortfolioFilterSelection,
    rawFilter: filter, // exposed for usePortfolioMultiFilteringWithSync to avoid a second subscription to the same atom

    setFilter: updateFilter,
    allLevels,
    strategyLevels,
    filterRowData,
    toggleMergeRows,
    levelSelectionOnly,
    isEmptyOrNoSubjects,
    supportsMergeLevels,
    getLevelsBasedOnFilter,
    hasMultiLevelPortfolioFiltering,
    wereLevelsFiltered,
    legendPosition,
    setLegendPosition,
    effectiveFilterLevels,
    portfolios,
  };
};

export const usePortfolioMultiFilteringWithSync = (blockId: BlockId) => {
  const result = usePortfolioMultiFiltering(blockId);
  const { effectiveFilterLevels, portfolios, rawFilter, setFilter, strategyLevels } = result;

  const portfoliosFingerprint = useMemo(
    () =>
      portfolios
        .map((p) => collectPortfolioIds(p).join('.'))
        .sort()
        .join(','),
    [portfolios],
  );

  const effectiveFilterLevelsStr = useMemo(() => effectiveFilterLevels.join(','), [effectiveFilterLevels]);

  // Refs keep rawFilter and setFilter out of the effect dep array, so the effect only fires when
  // portfolio structure or effective levels change — not on every user-initiated filter update.
  const rawFilterRef = useRef(rawFilter);
  rawFilterRef.current = rawFilter;

  const setFilterRef = useRef(setFilter);
  setFilterRef.current = setFilter;

  const effectiveFilterLevelsRef = useRef(effectiveFilterLevels);
  effectiveFilterLevelsRef.current = effectiveFilterLevels;

  const strategyLevelsRef = useRef(strategyLevels);
  strategyLevelsRef.current = strategyLevels;

  useEffect(() => {
    const rawFilter = rawFilterRef.current;

    // Only apply when explicitly in CUSTOM_SELECTION — other filter types don't store raw levels.
    const needsApplyEffectiveFilterLevels =
      rawFilter?.filterType === PortfolioStrategyFilterType.CUSTOM_SELECTION &&
      (rawFilter.levels?.length ?? 0) > effectiveFilterLevelsRef.current.length;

    if (needsApplyEffectiveFilterLevels && rawFilter) {
      // Levels exceed the new portfolio depth — truncate and remember the deepest dropped level so the
      // warning (derived from maxTruncatedLevel) persists across portfolio swaps until the depth returns.
      const removedNumeric = (rawFilter.levels ?? []).filter(
        (l): l is number => typeof l === 'number' && !effectiveFilterLevelsRef.current.includes(l),
      );
      setFilterRef.current({
        ...rawFilter,
        levels: effectiveFilterLevelsRef.current,
        maxTruncatedLevel:
          portfoliosFingerprint === '' || removedNumeric.length === 0 ? undefined : Math.max(...removedNumeric),
      });
    } else if (
      rawFilter?.maxTruncatedLevel !== undefined &&
      strategyLevelsRef.current.includes(rawFilter.maxTruncatedLevel)
    ) {
      // Clear only once the portfolio is deep enough to contain the previously-truncated level.
      // Filter-type-agnostic: auto-truncation may have promoted CUSTOM_SELECTION to ALL_STRATEGIES,
      // so the flag can survive on any filterType and must be cleared regardless.
      setFilterRef.current({ ...rawFilter, maxTruncatedLevel: undefined });
    }
  }, [effectiveFilterLevelsStr, portfoliosFingerprint]);

  return result;
};
